import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import { SERVER_TIMEOUT_MESSAGE } from 'utils/unityStrings';

import './ServerTimeoutBanner.scss';

/** How long the banner stays up before it hides itself. */
const SERVER_TIMEOUT_MESSAGE_TIMEOUT = 10000; //10 seconds

type ServerTimeoutValue = {
  /** Raise the banner. Idempotent while it is already showing. */
  showServerTimeout: () => void;
  /**
   * Raise the banner with another message, in the same place and for the same
   * time: an outcome the player must hear about that no reply channel reports
   * (EN-11023: a straddle tap that came too late). Each call is a new event:
   * announced again, even with the same words.
   */
  showNotice: (message: string) => void;
  /** Take it down now, and cancel the pending auto-hide. */
  hideServerTimeout: () => void;
};

/**
 * Defaults to no-ops so any surface without a provider — storybook, the demo
 * table, a test rendering one action button — behaves exactly as before rather
 * than throwing on a missing context.
 */
const ServerTimeoutContext = createContext<ServerTimeoutValue>({
  showServerTimeout: () => {},
  showNotice: () => {},
  hideServerTimeout: () => {}
});

export const useServerTimeout = (): ServerTimeoutValue => useContext(ServerTimeoutContext);

/**
 * Owns the "taking longer than expected" banner for ONE table.
 *
 * It lives here, above the action slot, and NOT inside PokerActions — which is
 * where it used to live, holding the visibility in local state and rendering
 * the portal from its own subtree.
 *
 * UserActions renders exactly one action panel at a time and swaps between
 * them: PokerActions, MultiRunActions during a run-it-twice vote window, or
 * nothing at all while the boards run out. So a banner owned by PokerActions
 * could not be raised during a vote window (nothing that could render it was
 * mounted), and an already-showing banner was DELETED mid-display the moment a
 * hand went all-in and the vote panel took the slot — the two failures QA saw.
 *
 * The vote window is the case that needs it most: `multiRunVote` refuses to
 * send on a dead socket, the emit is fire-and-forget with no ack channel, and
 * the window is only seconds long. Without this the player taps and nothing
 * whatsoever happens.
 *
 * Mounted per table, so the timer and the visibility follow the table they
 * belong to; the portal below is what keeps only the focused one painting.
 */
export const ServerTimeoutProvider = ({ children }: { children: ReactNode }) => {
  const [isShowing, setIsShowing] = useState(false);
  const [message, setMessage] = useState(SERVER_TIMEOUT_MESSAGE);
  // Counts notices raised, so a repeat of the same words is announced again.
  const [announcement, setAnnouncement] = useState(0);
  // The message up right now, or null while the banner is down.
  const shownMessageRef = useRef<string | null>(null);
  const hideTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isActiveTable = useIsActiveTable();

  const hideServerTimeout = useCallback(() => {
    setIsShowing(false);
    // MUST clear it: the show path treats it as "already showing" and bails,
    // so leaving it set here would make a dismissed banner unshowable for the
    // rest of the session.
    shownMessageRef.current = null;

    if (hideTimeoutIdRef.current) {
      clearTimeout(hideTimeoutIdRef.current);
      hideTimeoutIdRef.current = null;
    }
  }, []);

  // Every notice is its own event (a straddle refused on one hand, then on the
  // next), so it replaces whatever is up, is announced again and gets a fresh
  // auto-hide, even with the same words.
  const showNotice = useCallback((text: string) => {
    if (hideTimeoutIdRef.current) clearTimeout(hideTimeoutIdRef.current);
    shownMessageRef.current = text;
    setMessage(text);
    setAnnouncement((count) => count + 1);
    setIsShowing(true);

    hideTimeoutIdRef.current = setTimeout(() => {
      hideTimeoutIdRef.current = null;
      shownMessageRef.current = null;
      setIsShowing(false);
    }, SERVER_TIMEOUT_MESSAGE_TIMEOUT);
  }, []);

  const showServerTimeout = useCallback(() => {
    // Idempotent while it is already showing. Several paths can raise this for
    // the same turn — the ack timeout at ~2s, the turn-change effect when the
    // clock acted for the player, a refused vote — and raising it twice re-arms
    // the auto-hide, so the banner outstays its welcome.
    if (shownMessageRef.current === SERVER_TIMEOUT_MESSAGE) return;
    showNotice(SERVER_TIMEOUT_MESSAGE);
  }, [showNotice]);

  // The provider outlives the action panels but not the table. Closing a table
  // mid-banner would otherwise leave a timer holding a setState on an unmounted
  // tree.
  useEffect(
    () => () => {
      if (hideTimeoutIdRef.current) clearTimeout(hideTimeoutIdRef.current);
    },
    []
  );

  const value = useMemo(
    () => ({ showServerTimeout, showNotice, hideServerTimeout }),
    [showServerTimeout, showNotice, hideServerTimeout]
  );

  return (
    <ServerTimeoutContext.Provider value={value}>
      {children}
      {/* Portalled to <body> in the standard toast position (EN-10538 AC2):
          it used to live in the action bar and translate up 170px on show —
          directly onto the POV cards and stack, the one place a player is
          looking while an action hangs (Cory 8/19). A portal rather than a
          move because every ancestor here can carry a transform (the table
          swipe track, grid cells), and position:fixed inside a transformed
          ancestor anchors to that ancestor, not the viewport. */}
      {/* ONE banner, from the table it belongs to (JRam review, #3720).
          Visibility is per-table state and the grid mounts one provider per
          open table — including cells scrolled off screen. All of them
          portalled to <body> at the same fixed coordinates, so two
          simultaneous timeouts composited into unreadable overlapping text and
          none of them named a table. Only the focused table paints it, which
          is also the only table whose identity the player can infer from the
          banner's position. */}
      {isActiveTable &&
        createPortal(
          <>
            {/* The announcement, for anyone who cannot see the toast.
                It cannot live on the banner itself: `.server-timeout` is
                `visibility: hidden` until `.show`, which takes the whole
                subtree out of the accessibility tree — a live region that is
                not in the tree when its content changes never fires. So the
                text is carried by a region that is always in the tree and
                merely off-screen, and appears there only while the banner is
                up. `status`/`polite` rather than `alert`/`assertive`: this is
                a slow response, not a lost connection, and ConnectionWarning
                already owns the assertive announcement for that
                (ConnectionWarning.tsx:165). */}
            <div
              role='status'
              aria-live='polite'
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap'
              }}>
              {/* Keyed per notice: a new text node is what makes a screen
                  reader announce a repeat of the same words. */}
              {isShowing ? <span key={announcement}>{message}</span> : ''}
            </div>
            <div className={`server-timeout ${isShowing ? 'show' : ''}`}>
              <span className='server-timeout__text'>{message}</span>
              {/* The banner itself stays pass-through so it can never swallow a
                tap meant for the table nav strip underneath; the button
                re-enables pointer events just for itself (same contract the
                fixed chrome clusters use). */}
              <button
                type='button'
                className='server-timeout__dismiss'
                aria-label='Dismiss message'
                onClick={hideServerTimeout}>
                &times;
              </button>
            </div>
          </>,
          document.body
        )}
    </ServerTimeoutContext.Provider>
  );
};

export default ServerTimeoutProvider;
